import type { UserApiId } from '@/model/User.model';
import type { UserGroupRow } from '@/model/UserGroups.model';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { featuresSelector } from '@/support/Selectors';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

interface UseUserGroupsOutput {
  enabled: boolean;
  groups: Array<UserGroupRow>;
  groupsByUser: Map<UserApiId, Array<UserGroupRow>>;
  loading: boolean;
  loaded: boolean;
  failed: boolean;
  refetch: () => void;
}

/**
 * Every group on the team in one request, plus the inverse mapping of user to groups.
 *
 * The Users table needs each row's groups, and `GET /api/v1/user-groups` returns `member_api_ids`,
 * so inverting one response is cheaper than a request per user. `perPage: -1` is the internal API's
 * "no limit" convention, which keeps the mapping complete rather than page-shaped.
 *
 * The request is skipped entirely when the `user_groups` flag is off — the endpoint is gated on the
 * same flag and would answer 403, and a tenant without the feature must see no change at all.
 */
export const useUserGroups = (): UseUserGroupsOutput => {
  const features = useSelector(featuresSelector);
  const enabled = features.user_groups === true;

  const { response, loading, invoke } = useApiHook({
    endpoint: 'GET /api/v1/user-groups',
    query: { perPage: -1 },
    options: { invokeOnInit: enabled, onError: { toast: false, capture: true, throw: false } },
  });

  const groups = useMemo(() => (response?.type === ResponseType.Success ? response.body.data : []), [response]);

  const groupsByUser = useMemo(() => {
    const byUser = new Map<UserApiId, Array<UserGroupRow>>();
    groups.forEach((group) => {
      group.member_api_ids.forEach((memberApiId) => {
        const existing = byUser.get(memberApiId);
        if (existing === undefined) {
          byUser.set(memberApiId, [group]);
        } else {
          existing.push(group);
        }
      });
    });
    return byUser;
  }, [groups]);

  const refetch = useCallback(() => {
    if (enabled) {
      invoke().catch(() => undefined);
    }
  }, [enabled, invoke]);

  return {
    enabled,
    groups,
    groupsByUser,
    loading,
    loaded: response?.type === ResponseType.Success,
    failed: response?.type === ResponseType.Error,
    refetch,
  };
};
